// Export V6 — per-clip video readers for the on-device timeline renderer.
// loadDemux(url) parses an MP4/MOV once (mp4box) and keeps the encoded samples in memory;
// createClipReader() decodes forward from the nearest keyframe and returns the frame shown at
// any source time (seeks only when time jumps backwards or far ahead). Non-demuxable sources
// fall back to an HTMLVideoElement seeking reader — still 100% on the device.
import { createFile, DataStream } from "mp4box";

function descriptionFor(file, track) {
  const trak = file.getTrackById(track.id);
  for (const entry of trak.mdia.minf.stbl.stsd.entries) {
    const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
    if (box) {
      const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
      box.write(stream);
      return new Uint8Array(stream.buffer, 8);
    }
  }
  return null;
}

export async function fetchWithProgress(url, onLoad) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`SOURCE_FETCH_${resp.status}`);
  const total = Number(resp.headers.get("content-length")) || 0;
  if (!total || !resp.body || !onLoad) return resp.arrayBuffer();
  const parts = []; let got = 0;
  const reader = resp.body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value); got += value.length;
    onLoad(Math.min(1, got / total));
  }
  const out = new Uint8Array(got); let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out.buffer;
}

export async function loadDemux(buf) {
  const file = createFile();
  const samples = [];
  file.onSamples = (id, user, ss) => {
    for (const s of ss) samples.push({ t: s.cts / s.timescale, dur: s.duration / s.timescale, key: s.is_sync, data: s.data });
  };
  // mp4box 2.x only emits samples when extraction is started from inside onReady.
  const ready = new Promise((resolve, reject) => {
    file.onError = (e) => reject(new Error("mp4box: " + e));
    file.onReady = (info) => {
      const tr = info.videoTracks?.[0];
      if (!tr) return reject(new Error("NO_VIDEO_TRACK"));
      file.setExtractionOptions(tr.id, null, { nbSamples: Infinity });
      file.start();
      resolve(tr);
    };
  });
  const ab = buf.slice(0); ab.fileStart = 0;
  file.appendBuffer(ab); file.flush();
  const track = await ready;
  if (!samples.length) throw new Error("NO_SAMPLES");
  const cfg = { codec: track.codec, codedWidth: track.video?.width || track.track_width, codedHeight: track.video?.height || track.track_height };
  const description = descriptionFor(file, track);
  if (description) cfg.description = description;
  const sup = await window.VideoDecoder.isConfigSupported(cfg).catch(() => null);
  if (!sup?.supported) throw new Error("DECODER_UNSUPPORTED");
  return { cfg, samples, keyIdx: samples.map((s, i) => (s.key ? i : -1)).filter((i) => i >= 0) };
}

export function createClipReader(dm) {
  const { samples, keyIdx } = dm;
  const pending = [];
  let cur = null, curT = -1, feedIdx = 0, ended = false, flushing = null, err = null, wake = null;
  const notify = () => { if (wake) { const w = wake; wake = null; w(); } };
  let decoder;
  const open = () => {
    decoder = new window.VideoDecoder({
      output: (f) => { pending.push(f); notify(); },
      error: (e) => { err = e; notify(); },
    });
    decoder.configure(dm.cfg);
  };
  open();
  const seek = (t) => {
    let k = keyIdx[0] || 0;
    for (const i of keyIdx) { if (samples[i].t <= t + 1e-4) k = i; else break; }
    try { decoder.close(); } catch { /* ignore */ }
    pending.splice(0).forEach((f) => f.close());
    feedIdx = k; ended = false; flushing = null;
    open();
  };
  const waitOut = () => new Promise((r) => { wake = r; setTimeout(notify, 30); });
  return {
    async frameAt(t) {
      const tol = 1e-3;
      if (err) throw err;
      if (cur && t < curT - 0.25) seek(t);
      else if (cur && t > curT + 3) {
        const nextKey = keyIdx.find((i) => i >= feedIdx && samples[i].t <= t);
        if (nextKey !== undefined) seek(t);
      }
      for (;;) {
        if (err) throw err;
        pending.sort((a, b) => a.timestamp - b.timestamp);
        while (pending.length && pending[0].timestamp / 1e6 <= t + tol) {
          if (cur) cur.close();
          cur = pending.shift(); curT = cur.timestamp / 1e6;
        }
        if (!cur && pending.length) { cur = pending.shift(); curT = cur.timestamp / 1e6; }
        if (pending.length || ended) return cur;
        if (feedIdx < samples.length) {
          while (feedIdx < samples.length && decoder.decodeQueueSize < 6 && pending.length < 6) {
            const s = samples[feedIdx++];
            decoder.decode(new window.EncodedVideoChunk({ type: s.key ? "key" : "delta", timestamp: Math.round(s.t * 1e6), duration: Math.round(s.dur * 1e6), data: s.data }));
          }
          await waitOut();
        } else {
          if (!flushing) flushing = decoder.flush().then(() => { ended = true; notify(); }).catch((e) => { err = e; notify(); });
          await waitOut();
        }
      }
    },
    close() {
      try { decoder.close(); } catch { /* ignore */ }
      pending.splice(0).forEach((f) => f.close());
      if (cur) { try { cur.close(); } catch { /* ignore */ } cur = null; }
    },
  };
}

export async function createSeekReader(url) {
  const v = document.createElement("video");
  v.crossOrigin = "anonymous"; v.muted = true; v.preload = "auto"; v.playsInline = true; v.src = url;
  await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = () => rej(new Error("SOURCE_DECODE_FAILED")); });
  return {
    async frameAt(t) {
      const want = Math.max(0, Math.min(t, (v.duration || t) - 0.001));
      if (Math.abs(v.currentTime - want) > 1e-3) {
        await new Promise((r) => { const d = () => { v.removeEventListener("seeked", d); r(); }; v.addEventListener("seeked", d); v.currentTime = want; });
      }
      return v;
    },
    close() { try { v.removeAttribute("src"); v.load(); } catch { /* ignore */ } },
  };
}
